
import axios from 'axios'

const api = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  headers: {
    //'Content-Type': 'application/x-www-form-urlencoded'
    'Content-Type':'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // For FormData, let axios automatically set Content-Type with boundary
  if(config.data instanceof FormData){
    // Remove any existing Content-Type header to let axios set it automatically
    delete config.headers['Content-Type'];
  }
  return config
}, error => {
  return Promise.reject(error)
})

// 响应拦截器
api.interceptors.response.use(response => {
  return response.data
}, error => {
  // if (error.response.status === 400) {
  //   alert(`请求失败: ${error.response.data.message || '未知错误'}`);
  // }
  if (error.response.status === 401) {
    // 尝试刷新token
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken && error.config && !error.config._retry) {
      error.config._retry = true;
      return api.post('/auth/refresh', { refreshToken })
        .then(response => {
          if (response && response.accessToken) {
            // 保存新的token到localStorage和Vuex
            localStorage.setItem('token', response.accessToken);
            localStorage.setItem('refreshToken', response.refreshToken);
            
            // 如果有用户信息也保存
            if (response.user) {
              localStorage.setItem('user', JSON.stringify(response.user));
              localStorage.setItem('role', response.user.role);
            }
            
            // 重试原始请求
            const originalRequest = error.config;
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            return api(originalRequest);
          } else {
            // 刷新失败，跳转到登录页
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('role');
            window.location.href = '/login';
            return Promise.reject(error);
          }
        })
        .catch(refreshError => {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('role');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        });
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
  }
  return Promise.reject(error);
})

export default {
  // 认证相关API
  auth: {
    login(credentials) {
      return api.post('/auth/login', credentials)
    },
    register(userData) {
      return api.post('/auth/register', userData)
    },
    refresh(refreshTokenData) {
      return api.post('/auth/refresh', refreshTokenData)
    }
  },
  // 消息相关API
  messages: {
    send(data) {
      return api.post('/messages', data)
    },
    subscribe(userId) {
      // 使用fetch API实现带认证的SSE客户端
      return new Promise((resolve, reject) => {
        // 获取存储的token
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) {
          reject(new Error('未找到认证token'));
          return;
        }

        const url = `${process.env.VUE_APP_BASE_API}/messages/subscribe/${userId}`;
        const headers = {
          'Authorization': `Bearer ${token}`
        };

        fetch(url, {
          method: 'GET',
          headers: headers,
          credentials: 'same-origin',
          cache: 'no-cache'
        }).then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          // 模拟EventSource对象
          const eventSource = {
            onopen: () => {},
            onmessage: () => {},
            onerror: () => {},
            close: () => {}
          };

          // 触发open事件
          setTimeout(() => {
            eventSource.onopen();
            console.log('SSE connection established with auth');
          }, 0);

          // 读取流数据
          function readStream() {
            reader.read().then(({ done, value }) => {
              if (done) {
                eventSource.onerror(new Error('Stream closed'));
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              let position;

              // 处理接收到的SSE事件
              while ((position = buffer.indexOf('\n\n')) !== -1) {
                const chunk = buffer.substring(0, position);
                buffer = buffer.substring(position + 2);

                if (chunk.trim()) {
                  // 解析SSE事件格式
                  const lines = chunk.split('\n');
                  let eventName = 'message';
                  let eventData = '';

                  for (const line of lines) {
                    if (line.startsWith('event:')) {
                      eventName = line.substring(6).trim();
                    } else if (line.startsWith('data:')) {
                      eventData += line.substring(5).trim();
                    }
                  }

                  // 触发对应的事件
                  if (eventSource[`on${eventName}`]) {
                    const event = {
                      data: eventData
                    };
                    eventSource[`on${eventName}`](event);
                  }
                }
              }

              readStream();
            }).catch(error => {
              console.error('SSE read error:', error);
              eventSource.onerror(error);
              reject(error);
            });
          }

          readStream();
          resolve(eventSource);
        }).catch(error => {
          console.error('SSE connection error:', error);
          reject(error);
        });
      });
    },
    getMessages(pagination = {}, isRead = false) {
      const params = {
        pageNumber: pagination.currentPage - 1 || 0,
        pageSize: pagination.pageSize || 10,
        isRead
      }
      return api.get('/messages', { params })
    },
    getUnreadCount() {
      return api.get('/messages/unread-count')
    },
    markAsRead(id) {
      return api.put(`/messages/${id}/read`)
    },
    batchMarkAsRead(messageIds) {
      return api.put('/messages/read/batch', messageIds)
    },
    delete(id) {
      return api.delete(`/messages/${id}`)
    }
  },
  // 用户相关API
  user: {
    getUsersByStatus(status) {
      return api.get(`/users/getUsersByStatus/${status}`)
    },
    getAllUsers() {
      return api.get('/users/getAllUsers')
    },
    updateUser(user) {
      return api.post('/users/updateUser', user)
    },
    changePassword(user) {
      return api.post('/userInfo/changePassword', user)
    },
    assignUserPlacement(userAssignmentDto) {
      return api.post('/users/assignUserPlacement', userAssignmentDto)
    },
    setDepartment(organizationDto) {
      return api.post('/userInfo/department/setDepartment', organizationDto)
    },
    getDepartments() {
      return api.get('/userInfo/department/getDepartments')
    },
    deleteDepartment(id) {
      return api.delete(`/userInfo/department/${id}`)
    },
    setTeam(organizationDto) {
      return api.post('/userInfo/team/setTeam', organizationDto)
    },
    getTeams() {
      return api.get('/userInfo/team/getTeams')
    },
    deleteTeam(id) {
      return api.delete(`/userInfo/team/${id}`)
    }

  },
  // 客户相关API
  customers: {
    getAll() {
      return api.get('/customers')
    },
    create(customer) {
      return api.post('/customers', customer)
    },
    update(id, customer) {
      return api.put(`/customers/${id}`, customer)
    },
    delete(id) {
      return api.delete(`/customers/${id}`)
    },
    getById(id) {
      return api.get(`/customers/${id}`)
    },
    import(file) {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/customers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  },
  // 项目相关API
  projects: {
    getPaginProjects(pagination = {}, includedComplete, isWarning) {
      // 构建分页参数
      const params = {
        pageNumber: pagination.currentPage - 1 || 0,
        pageSize: pagination.pageSize || 10,
        includedComplete,
        isWarning
      }
      return api.get('/projects', { params })
    },
    getPaginProjectsByQuery(pagination = {}, query) {
      // 构建分页参数
      const params = {
        pageNumber: pagination.currentPage || 0,
        pageSize: pagination.pageSize || 10,
        ...query
      }
      return api.get('/projects/page', { params })
    },
    getAll(){
      return api.get('/projects/getAllProjects')
    },
    getByCustomer(customerId) {
      return api.get(`/projects/customer/${customerId}`)
    },
    create(project) {
      return api.post('/projects', project)
    },
    update(id, project) {
      return api.put(`/projects/${id}`, project)
    },
    delete(id) {
      return api.delete(`/projects/${id}`)
    },
    getById(id) {
      return api.get(`/projects/${id}`)
    },
    exportExcel(exportData) {
      return api.post('/projects/exportExcel', exportData, {
        responseType: 'blob'  // 关键：指定接收二进制Blob
    })
    },
    getCustomerLatestProject(customerId) {
      return api.get(`/projects/getCustomerLatestProject/${customerId}`)
    },
    getCustomerFirstProject(){
      return api.get('/projects/getCustomerFirstProject')
    },
    approved(id) {
      return api.post(`/projects/approved/${id}`)
    },
    accepted(acceptDto) {
      return api.post(`/projects/accepted`, acceptDto)
    },
    rejected(rejectDto) {
      return api.post(`/projects/rejected`, rejectDto)
    },
    checked(checkDto) {
      return api.post(`/projects/checked`, checkDto)
    },
    paid(paidDto) {
      return api.post(`/projects/paid`, paidDto)
    },
    audited(paymentId) {
      return api.post(`/projects/audited/${paymentId}`)
    },
    getProjectPayments(projectId) {
      return api.get(`/projects/payments/${projectId}`)
    }
  }
}
